/**
 * @fileoverview Diz se a triagem por IA está ligada para a empresa do usuário
 * (liberada pelo super admin e ligada no servidor). Uma consulta por empresa,
 * compartilhada entre as telas e válida por pouco tempo; relê quando a setting
 * "triageEnabled" muda (aviso por socket ou o próprio interruptor).
 */

import { useContext, useEffect, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const SETTING_KEY = "triageEnabled";
const TTL_MS = 60 * 1000;
// companyId -> { request: Promise<boolean>, at: número }
const cache = new Map();

const fetchEnabled = (companyId, force = false) => {
  const hit = cache.get(companyId);
  if (!force && hit && Date.now() - hit.at < TTL_MS) return hit.request;
  const request = api
    .get("/triage-status")
    .then(({ data }) => !!data?.enabled)
    .catch(() => {
      // Falha momentânea: não guarda, a próxima tela tenta de novo.
      cache.delete(companyId);
      return false;
    });
  cache.set(companyId, { request, at: Date.now() });
  return request;
};

/** Esquece o que foi lido (o interruptor chama depois de mudar a setting). */
export const invalidateTriageEnabled = () => cache.clear();

const useTriageEnabled = () => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  // "Apenas interno" não vê o atendimento (o backend também recusa).
  const internalOnly = user?.accessScope === "internal";
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!companyId || internalOnly) {
      setEnabled(false);
      return undefined;
    }
    let active = true;
    fetchEnabled(companyId).then((value) => active && setEnabled(value));

    const socket = socketManager.getSocket(companyId);
    const onSettings = (data) => {
      if (data?.setting?.key !== SETTING_KEY) return;
      fetchEnabled(companyId, true).then((value) => active && setEnabled(value));
    };
    socket.on(`company-${companyId}-settings`, onSettings);
    return () => {
      active = false;
      // disconnect() do socket gerenciado só solta os ouvintes deste uso.
      socket.disconnect();
    };
  }, [socketManager, companyId, internalOnly]);

  return enabled;
};

export default useTriageEnabled;
